<template>
    <div>
        <Modal
            ref="modal"
            name="worksite:worksite-progress-billing:setting-modal"
        >
            <div
                :class="$theme('modal.title.container')"
                class="space-y-6"
            >
                <h2 :class="$theme('modal.title.text')">
                    {{ $t('worksite_progress_billing.modal.lines.setting.title') }}
                </h2>
                <div class="px-8 mb-8 py-3 text-sm text-center text-neutral-500 bg-neutral-100 rounded">
                    <p>{{ $t('worksite_progress_billing.modal.lines.setting.text') }}</p>
                </div>
            </div>

            <form
                class="mb-4"
                @submit.prevent="submit"
            >
                <div
                    :class="$theme('modal.padding')"
                    class="space-y-6"
                >
                    <div class="w-full flex justify-center items-center">
                        <div class="text-neutral-500 font-thin flex relative h-[50px]">
                            <div class="flex items-center">
                                <WorksiteWorksiteProgressBillingItemSettingInputNumber
                                    :model-value="quantity"
                                    :max-quantity="leftQuantity"
                                    @inc="inc()"
                                    @dec="dec()"
                                    @update="(e) => updateQuantity(e)"
                                />
                            </div>

                            <div class="flex items-center">
                                <WorksiteWorksiteProgressBillingItemSettingCheckboxe
                                    v-for="(item, index) in items"
                                    :key="index"
                                    :item="item"
                                    @checked="(e) => selectMode(e)"
                                />
                            </div>
                        </div>
                    </div>

                    <div :class="$theme('modal.footer.button_container')">
                        <LoadingButton
                            :extra-class-name="$theme('modal.footer.button')"
                            :class-name="$theme('button.style.cancel')"
                            prevent-default
                            @clicked="useEvent('worksite:worksite-progress-billing:setting-modal:close')"
                            >{{ $t('actions.cancel') }}</LoadingButton
                        >
                        <LoadingButton
                            :extra-class-name="$theme('modal.footer.button')"
                            type="submit"
                        >
                            {{ $t('actions.save') }}
                        </LoadingButton>
                    </div>
                </div>
            </form>
        </Modal>
    </div>
</template>
<script lang="ts" setup>
    import { UnwrapNestedRefs } from 'vue';
    import { useCounter } from '@vueuse/core';
    import WorksiteProgressBillingForm from '../../../app/worksite/classes/WorksiteProgressBillingForm';
    import WorksiteProgressBillingLineForm from '../../../app/worksite/classes/WorksiteProgressBillingLineForm';
    import useDisableNumberInputScroll from '~/composables/useDisableNumberInputScroll';

    useDisableNumberInputScroll();

    export interface SettingItem {
        title: string;
        checked: boolean;
    }
    const form = ref<WorksiteProgressBillingForm | null>(null);

    const items = reactive<SettingItem[]>([
        { title: '%', checked: false },
        { title: 'Unit', checked: true },
    ]);

    useModal('worksite:worksite-progress-billing:setting-modal', (payload: WorksiteProgressBillingForm) => {
        form.value = payload;
        items[0].checked = false;
        items[1].checked = true;
        quantity.value = 0;
    });

    const isPercentage = computed(() => {
        const current = items.find((el: SettingItem) => el.title === '%') as SettingItem;
        return current.checked;
    });

    const leftQuantity = computed(() => {
        if (isPercentage.value) return 100;
        return form?.value?.getLinesLeftQuantity();
    });

    const { dec, inc, count: quantity } = useCounter(0, { min: 0 });

    const { t } = useTranslation();

    const selectMode = (selected: SettingItem) => {
        if (selected.checked) return;
        items.forEach((item) => (item.checked = item.title === selected.title));
        quantity.value = 0;
    };

    const notify = (line: UnwrapNestedRefs<WorksiteProgressBillingLineForm>) => {
        return useToasteoBasic(
            line.invoiceLine.title as string,
            `${t('worksite_progress_billing.modal.lines.setting.toast.unit_remaining', {
                unit: line.getLeftQuantity().toFixed(2),
            })}`
        );
    };

    const updateQuantity = (e: number) => {
        quantity.value = e;
    };

    const applyToLine = (line: UnwrapNestedRefs<WorksiteProgressBillingLineForm>) => {
        line.isPercentage = isPercentage.value;
        if (line.getLeftQuantity() === 0) return;
        if (quantity.value > line.getLeftQuantity()) {
            line.quantity = line.getLeftQuantity();
            return notify(line);
        }
        line.quantity = quantity.value;
    };

    const submit = async () => {
        form.value?.lines.forEach((line: UnwrapNestedRefs<WorksiteProgressBillingLineForm>) => applyToLine(line));
        useEvent('worksite:worksite-progress-billing:setting-modal:close');
    };
</script>
